import cronstrue from "cronstrue";
import { observer } from "mobx-react";
import { useEffect, useState } from "react";
import toast from "react-hot-toast";
import { useNavigate } from "react-router-dom";
import { globalStores, useGlobalStore } from "../../api/globalStores";

import {
  ClassTiming,
  dateToMonthDayString,
} from "../Utility/helpers";
import Modal from "../Utility/Modal";

interface IScheduledLessonCard {
  classTiming: ClassTiming;
  classIntervalString: string;
  duration: string;
  isPaid: boolean;
}

export default observer(ScheduledLessonCard);

function ScheduledLessonCard(props: IScheduledLessonCard) {
  const navigate = useNavigate();
  const globalStores = useGlobalStore();
  const [openStartRating, setOpenStartRating] = useState(false);
  const [now, setNow] = useState(() => new Date());

  // Refresh "now" once a minute so the join button activates without a page reload.
  useEffect(() => {
    const t = setInterval(() => setNow(new Date()), 60_000);
    return () => clearInterval(t);
  }, []);

  const { start, end, class: curClass } = props.classTiming;

  const startDate = curClass.class.startDate
    ? dateToMonthDayString(curClass.class.startDate)
    : "unknown";
  const endDate = curClass.class.endDate
    ? dateToMonthDayString(curClass.class.endDate)
    : "unknown";

  // Mirrors JOIN_WINDOW_MS in beta-backend/src/100ms/lessonResolver.ts.
  // Keep these in sync so the card's enabled state matches the server's
  // willingness to allocate a room.
  const JOIN_WINDOW_MS = 6 * 60 * 60 * 1000;
  const isJoinable =
    now.getTime() >= start.getTime() - JOIN_WINDOW_MS &&
    now.getTime() <= end.getTime() + JOIN_WINDOW_MS;

  const handleSelectTimeSlot = async (
    e: React.MouseEvent<HTMLDivElement, MouseEvent>
  ) => {
    if (!curClass.class.id) {
      throw new Error("Class ID is undefined");
    }

    globalStores.curClass.init(curClass.class.id);
    await globalStores.curTalkingParty.joinActiveLessonForClass(curClass.class.id);
    setOpenStartRating(true);

    if (e.ctrlKey) {
      await globalStores.curTalkingParty.setFeeling("👓");
      await globalStores.curEPanel.clear();
      navigate(`/student/lesson`);
    }
  };

  return (
    <>
      <Modal
        open={openStartRating}
        onClose={() => {
          setOpenStartRating(false);
        }}
      >
        <StartRating />
      </Modal>
      <div
        onClick={
          isJoinable
            ? (e) =>
                toast.promise(handleSelectTimeSlot(e), {
                  success: "Time Slot loaded",
                  loading: "Loading time slot",
                  error: (err) => err.message,
                })
            : undefined
        }
        className={`w-full bg-[#FFD6DD] rounded-2xl shadow-2xl relative overflow-clip transition ease-in-out z-20 ${
          isJoinable ? "cursor-pointer hover:scale-105" : "cursor-not-allowed opacity-70"
        }`}
        aria-disabled={!isJoinable}
      >
        <div className="flex flex-col gap-2 p-6 text-brij-Brown z-20">
          <div className="inline-flex flex-col font-semibold text-xlarge">
            Every {props.classIntervalString}
          </div>
          <div className="inline-flex flex-col text-xlarge">
            {startDate} to {endDate}
          </div>

          <div className="inline-flex gap-1">
            <svg
              className="stroke-brij-Brown"
              width="16"
              height="16"
              viewBox="0 0 16 16"
              fill="none"
              xmlns="http://www.w3.org/2000/svg"
            >
              <path
                d="M8 14C11.3137 14 14 11.3137 14 8C14 4.68629 11.3137 2 8 2C4.68629 2 2 4.68629 2 8C2 11.3137 4.68629 14 8 14Z"
                strokeLinecap="round"
                strokeLinejoin="round"
              />
              <path
                d="M8 4.66663V7.99996L10 9.99996"
                strokeLinecap="round"
                strokeLinejoin="round"
              />
            </svg>
            <p className="text-normal">Min {props.duration} each</p>
          </div>
          <div className="inline-flex flex-col items-center text-xlarge font-medium">
            <p>{isJoinable ? "Click to Join" : "Class hasn't started yet"}</p>
          </div>
        </div>
        <div
          className="absolute -right-20 inset-y-0 scale-90 origin-right z-10"
          style={{ zIndex: -1 }}
        >
          <BackgroundArt />
        </div>
      </div>
    </>
  );
}

function StartRating() {
  const navigate = useNavigate();

  const _handleRatingSelection = async (selection: string) => {
    await globalStores.curTalkingParty.setFeeling(selection);
    await globalStores.curEPanel.clear();
    navigate("/student/lesson");
  };

  const handleRatingSelection = (selection: string) => {
    toast.promise(_handleRatingSelection(selection), {
      loading: "Loading Talking Party",
      success: "Talking Party Loaded!",
      error: (e) => e.message,
    });
  };

  return (
    <div className="inline-flex">
      <div className="flex flex-col items-center justify-start gap-8 overflow-clip rounded-[20px] bg-[rgba(255,254,252,1)] p-8 drop-shadow-lg">
        <div className="flex flex-col items-center justify-start gap-3">
          <p className="text-center font-['Poppins'] text-xl font-medium leading-[1.4] text-[rgba(64,52,52,1)]">
            {"How are you "}
            <br />
            feeling today?
          </p>
          <p className="text-center font-['Poppins'] text-base leading-[1.5] text-[rgba(109,109,109,1)]">
            Select One
          </p>
        </div>
        <div className="flex flex-col items-start justify-start">
          <div className="flex w-full">
            <div className="flex w-full flex-col items-center justify-center gap-[18px] overflow-clip">
              <div className="flex">
                <div className="flex items-center justify-center gap-3 overflow-clip bg-[rgba(255,254,252,1)]">
                  <div
                    className="cursor-pointer"
                    onClick={() => {
                      handleRatingSelection("😃");
                    }}
                  >
                    <p className="font-['Poppins'] text-6xl font-semibold leading-[1.5] text-black">
                      😃
                    </p>
                  </div>
                  <div
                    className="cursor-pointer"
                    onClick={() => {
                      handleRatingSelection("😐");
                    }}
                  >
                    <p className="font-['Poppins'] text-6xl font-semibold leading-[1.5] text-black">
                      😐
                    </p>
                  </div>
                </div>
              </div>
            </div>
          </div>
          <div className="flex w-full">
            <div className="flex w-full items-center justify-center gap-[18px] overflow-clip bg-[rgba(255,254,252,1)]">
              <div
                className="cursor-pointer"
                onClick={() => {
                  handleRatingSelection("😕");
                }}
              >
                <p className="font-['Poppins'] text-6xl font-semibold leading-[1.5] text-black">
                  😕
                </p>
              </div>
              <div
                className="cursor-pointer"
                onClick={() => {
                  handleRatingSelection("😴");
                }}
              >
                <p className="font-['Poppins'] text-6xl font-semibold leading-[1.5] text-black">
                  😴
                </p>
              </div>
            </div>
          </div>
          <div className="flex w-full">
            <div className="flex w-full items-center justify-center gap-[18px] overflow-clip bg-[rgba(255,254,252,1)]">
              <div
                className="cursor-pointer"
                onClick={() => {
                  handleRatingSelection("😰");
                }}
              >
                <p className="font-['Poppins'] text-6xl font-semibold leading-[1.5] text-black">
                  😰
                </p>
              </div>
              <div
                className="cursor-pointer"
                onClick={() => {
                  handleRatingSelection("😎");
                }}
              >
                <p className="font-['Poppins'] text-6xl font-semibold leading-[1.5] text-black">
                  😎
                </p>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}

function BackgroundArt() {
  return (
    <svg
      style={{ opacity: "20%" }}
      width="323px"
      height="209px"
      preserveAspectRatio="none"
      viewBox="0 0 323 209"
      fill="none"
      xmlns="http://www.w3.org/2000/svg"
    >
      <path
        opacity="0.35"
        d="M303.533 33.6384H168.958C168.958 33.6384 175.595 13.3586 189.448 13.3586C199.821 13.3586 202.324 22.1473 212.282 22.1473C222.241 22.1473 228.912 0 243.971 0C259.814 0 267.63 25.3357 281.92 28.1127C299.466 31.5189 303.533 33.6384 303.533 33.6384Z"
        fill="#FFFEFC"
      />
      <path
        d="M182.942 209H8.99278C8.99278 209 17.5717 184.57 35.4779 184.57C48.8868 184.57 52.1215 195.155 64.994 195.155C77.8665 195.155 86.4898 168.475 105.953 168.475C126.432 168.475 136.534 198.997 155.007 202.341C177.684 206.445 182.942 209 182.942 209Z"
        fill="#F2F6F9"
      />
      <path
        d="M177.236 173.77H322.841C322.841 173.77 318.079 151.448 304.489 151.448C292.762 151.448 282.84 164.758 272.952 164.758C263.064 164.758 258.132 135.278 238.818 135.278C220.851 135.278 206.424 166.216 192.347 168.946C180.213 171.3 177.236 173.77 177.236 173.77Z"
        fill="#F2F6F9"
      />
      <path
        opacity="0.35"
        d="M-0.000114441 57.6587L5.84826 59.2156C6.10236 63.7576 7.81856 68.0955 10.7427 71.5808C13.6669 75.066 17.6398 77.5115 22.0684 78.5515C26.4971 79.5917 31.1435 79.1706 35.312 77.3513C39.4825 75.5321 42.9512 72.4123 45.1998 68.4581L46.1012 67.8914C49.342 65.8554 51.641 62.6154 52.4941 58.8841C53.3451 55.1528 52.6817 51.2358 50.6448 47.9947C50.2758 47.4077 49.6889 46.9908 49.0113 46.836C48.3357 46.6809 47.6258 46.8003 47.037 47.1679L42.7213 49.8804C41.9267 47.9365 40.7167 46.1898 39.1759 44.762C38.6677 44.2909 37.9921 44.0408 37.2984 44.0664C36.6047 44.0922 35.9513 44.3915 35.4773 44.8991L23.2865 58.0298H19.5113C19.4972 56.3971 18.9002 54.8229 17.8294 53.5907C16.7585 52.3587 15.2823 51.5492 13.6669 51.3084C12.0516 51.0674 10.4039 51.4106 9.01848 52.2766C7.63504 53.1424 6.60452 54.4738 6.11446 56.0313L5.93699 56.0777L-0.000114441 57.6587Z"
        fill="#FFFEFC"
      />
      <path
        opacity="0.35"
        d="M76.5897 32.981L80.0503 33.8966C80.2015 36.5829 81.218 39.1481 82.9483 41.209C84.6786 43.2698 87.028 44.7158 89.6477 45.3309C92.2674 45.946 95.0141 45.6971 97.4805 44.6218C99.9469 43.5463 102 41.7018 103.331 39.3639L103.865 39.0291C105.783 37.8247 107.144 35.9079 107.649 33.7C108.155 31.4919 107.762 29.1739 106.558 27.2557C106.34 26.9078 105.991 26.6607 105.592 26.569C105.19 26.477 104.771 26.5478 104.422 26.7656L101.867 28.379C101.397 27.2298 100.681 26.1973 99.7694 25.3539C99.4689 25.0746 99.0696 24.9262 98.6582 24.9413C98.2488 24.9564 97.8616 25.1339 97.5813 25.4346L90.3656 33.2069H88.1332C88.1332 32.2353 87.7863 31.2957 87.153 30.5594C86.5178 29.8229 85.6405 29.3389 84.6806 29.1953C83.7186 29.0519 82.7385 29.2584 81.9177 29.7775C81.0949 30.2966 80.4879 31.0936 80.2056 32.0231L80.1007 32.0514L76.5897 32.981Z"
        fill="#FFFEFC"
      />
      <path
        d="M201.508 46.3346L155.855 95.5147C150.382 101.41 147.475 109.239 147.775 117.277C148.073 125.316 151.554 132.907 157.449 138.38C163.345 143.852 171.173 146.759 179.212 146.46C187.25 146.161 194.841 142.681 200.314 136.785L217.367 118.415C221.955 113.473 225.524 107.675 227.871 101.353C230.218 95.0317 231.297 88.3095 231.047 81.5707C230.796 74.8319 229.22 68.2085 226.41 62.0788C223.599 55.949 219.609 50.4327 214.666 45.8452C212.856 44.1657 210.453 43.2737 207.986 43.3654C205.518 43.4572 203.188 44.5252 201.508 46.3346Z"
        fill="#FFADBC"
      />
      <path
        d="M254.756 81.783L264.835 121.55L200.634 123.714L198.492 115.27L254.756 81.783Z"
        fill="#5E93D1"
      />
      <path
        d="M141.114 188.738L167.224 184.731L163.703 175.299L153.194 177.737L141.114 188.738Z"
        fill="#323232"
      />
      <path
        d="M114.635 181.127L140.743 177.122L137.222 167.69L126.715 170.128L114.635 181.127Z"
        fill="#323232"
      />
      <path
        d="M172.864 83.9107L150.052 78.0502L121.177 113.223C119.414 115.396 118.107 117.9 117.329 120.588C116.55 123.275 116.321 126.091 116.649 128.869L121.768 170.921L140.478 170.719L134.856 126.775L162.529 107.415C167.599 102.979 170.958 96.7389 171.981 89.87L172.864 83.9107Z"
        fill="#211F5C"
      />
      <path
        d="M171.715 43.7099C163.975 39.9689 151.28 39.6765 145.7 40.9954C140.119 42.3144 134.449 64.6109 134.449 64.6109L121.669 82.7612L134.186 90.8037L149.148 69.3441L143.207 64.0382C143.207 64.0382 148.636 72.4377 150.029 78.5463H175.831C175.831 78.5463 174.193 66.4461 173.735 59.5248"
        fill="#FF8FA2"
      />
      <path
        d="M120.328 118.384C106.874 118.384 95.9683 107.478 95.9683 94.0243C95.9683 80.5708 106.874 69.6647 120.328 69.6647C133.781 69.6647 144.687 80.5708 144.687 94.0243C144.687 107.478 133.781 118.384 120.328 118.384Z"
        fill="#48A9BC"
      />
      <path
        d="M95.9681 94.0242C95.9681 135.423 129.528 168.985 170.926 168.985C212.325 168.985 245.886 135.423 245.886 94.0242"
        fill="#48A9BC"
      />
      <path
        d="M145.373 50.9296L141.589 60.2669L145.596 70.4996"
        stroke="#FFE0E6"
      />
      <path
        d="M175.722 78.4998H152.242L132.075 119.886C130.851 122.414 130.142 125.16 129.989 127.965C129.836 130.769 130.243 133.576 131.185 136.222L146.093 178.001H167.185L153.784 128.254L170.942 103.904C174.897 98.243 176.755 91.3804 176.196 84.4975L175.722 78.4998Z"
        fill="#211F5C"
      />
      <path
        d="M137.244 83.4265H135.546C134.853 83.4122 134.161 83.4848 133.487 83.6423C132.228 83.9105 131.766 84.8241 129.655 84.9269C127.269 85.0459 128.983 87.1211 130.133 87.347C131.03 87.5164 132.111 87.0707 132.835 87.6273C132.634 88.1516 132.595 89.3032 129.911 91.5699C128.374 92.8667 125.676 93.2236 126.281 96.4342C127.122 98.8885 130.948 99.0337 132 98.6021C135.076 97.3417 137.508 94.7704 138.776 92.6831C139.353 91.6786 139.849 90.6307 140.263 89.5492"
        fill="#FFCCD7"
      />
      <path
        d="M142.291 72.9075L130.978 81.4603L142.055 93.2922L167.883 75.6522C167.883 75.6522 176.756 54.2652 170.608 48.2554"
        fill="#FF8FA2"
      />
      <path
        d="M142.83 28.502C138.639 28.502 135.241 24.1626 135.241 18.8097C135.241 13.4569 138.639 9.11749 142.83 9.11749C147.021 9.11749 150.419 13.4569 150.419 18.8097C150.419 24.1626 147.021 28.502 142.83 28.502Z"
        fill="#24272D"
      />
      <path
        d="M154.285 45.6681C153.156 45.6675 152.058 45.2986 151.157 44.6172C150.257 43.9358 149.604 42.9792 149.297 41.8928L144.479 24.8921C144.273 24.2308 144.202 23.5351 144.269 22.8458C144.337 22.1565 144.542 21.4877 144.871 20.8789C145.201 20.2699 145.65 19.7333 146.191 19.3006C146.731 18.8678 147.353 18.5477 148.02 18.3591C148.686 18.1706 149.383 18.1174 150.071 18.2027C150.758 18.288 151.421 18.5101 152.021 18.8558C152.621 19.2016 153.146 19.664 153.565 20.2158C153.983 20.7676 154.287 21.3976 154.458 22.0687L159.276 39.0715C159.494 39.8431 159.53 40.6548 159.382 41.4429C159.234 42.2308 158.905 42.9738 158.421 43.6135C157.938 44.2532 157.313 44.7721 156.595 45.1296C155.877 45.4872 155.086 45.6735 154.285 45.6741V45.6681Z"
        fill="#FFCCD7"
      />
      <path
        d="M149.545 38.1176L148.64 38.3697C147.586 38.6607 146.482 38.7159 145.404 38.5316C144.327 38.3471 143.303 37.9276 142.407 37.3028C141.51 36.6781 140.762 35.8635 140.216 34.9167C139.669 33.97 139.338 32.9145 139.246 31.8255L138.788 26.4288C138.641 24.6874 139.111 22.9498 140.117 21.5206C141.122 20.0913 142.599 19.0616 144.288 18.6121C146.195 18.1072 148.224 18.3804 149.93 19.3717C151.636 20.3628 152.878 21.9909 153.383 23.8979L154.746 29.0445C155.248 30.9369 154.983 32.9506 154.009 34.6491C153.036 36.3475 151.432 37.5938 149.545 38.1176Z"
        fill="#FFCCD7"
      />
      <path
        d="M142.424 27.0499L141.142 31.8033L143.017 31.8376"
        stroke="#25282D"
      />
      <path
        d="M144.687 28.8973C144.185 28.8973 143.777 28.4901 143.777 27.9878C143.777 27.4854 144.185 27.0782 144.687 27.0782C145.189 27.0782 145.596 27.4854 145.596 27.9878C145.596 28.4901 145.189 28.8973 144.687 28.8973Z"
        fill="#24272D"
      />
      <path
        d="M140.313 28.8973C139.811 28.8973 139.403 28.4901 139.403 27.9878C139.403 27.4854 139.811 27.0782 140.313 27.0782C140.815 27.0782 141.222 27.4854 141.222 27.9878C141.222 28.4901 140.815 28.8973 140.313 28.8973Z"
        fill="#24272D"
      />
      <path
        d="M144.947 33.6384C144.947 33.6384 146.065 33.5759 146.524 32.6058"
        stroke="#25282D"
      />
      <path
        d="M186.991 32.848C188 44.6295 178.033 40.7998 178.033 40.7998C178.033 40.7998 184.4 53.7309 170.152 52.6519C155.904 51.573 152.016 41.4512 152.567 35.6633C152.786 33.3562 153.978 31.5694 155.344 29.8452C147.535 28.6452 147.654 23.2889 147.654 23.2889C147.654 23.2889 135.134 28.1532 135.235 18.5597C135.336 8.96633 145.212 5.89087 151.048 5.99171C152.277 6.0174 153.496 6.22885 154.662 6.6189C158.419 7.84101 161.329 9.83351 166.314 9.91216C180.088 10.13 181.411 23.1376 181.411 23.1376C181.411 23.1376 186.314 24.874 186.991 32.848Z"
        fill="#24272D"
      />
      <path
        d="M151.302 31.5303L151.3 31.5295C149.596 30.8108 148.796 28.8463 149.515 27.1416L150.699 24.3336C151.418 22.6288 153.382 21.8296 155.087 22.5484L155.089 22.5492C156.793 23.2677 157.593 25.2324 156.874 26.9371L155.69 29.7449C154.972 31.4497 153.007 32.2489 151.302 31.5303Z"
        fill="#FFCCD7"
      />
      <path d="M154.327 24.6643L152.645 28.6453" stroke="#25282D" />
      <path
        d="M75.279 92.7014L96.3917 98.324L99.6184 99.1811C100.469 99.1815 101.312 99.0143 102.099 98.6888C102.885 98.3633 103.599 97.8861 104.2 97.2842C104.803 96.6824 105.279 95.9679 105.604 95.1815C105.931 94.3952 106.096 93.5525 106.096 92.7014H75.279Z"
        fill="#FFADBC"
      />
      <path
        d="M117.113 86.2216C115.621 86.2216 114.413 85.0126 114.413 83.5212C114.413 82.0299 115.621 80.8209 117.113 80.8209C118.603 80.8209 119.813 82.0299 119.813 83.5212C119.813 85.0126 118.603 86.2216 117.113 86.2216Z"
        fill="#FFADBC"
      />
      <path
        d="M75.2791 92.7014H106.094C106.094 91.8504 105.929 91.0074 105.604 90.2209C105.277 89.4346 104.801 88.7199 104.198 88.1181C103.597 87.5161 102.883 87.0388 102.097 86.7135C101.31 86.3882 100.467 86.221 99.6164 86.2218L96.3897 87.0809L75.2791 92.7014Z"
        fill="#FFADBC"
      />
      <path
        d="M137.373 76.6243L153.486 64.623L157.328 53.5332"
        stroke="#FFE0E6"
      />
      <path
        d="M163.751 78.4776C163.751 78.4776 167.851 76.0858 168.911 72.96C169.972 69.8341 174.155 56.1428 174.155 56.1428"
        stroke="#FFE0E6"
      />
      <path
        d="M151.536 124.123L153.784 128.25L151.536 131.107"
        stroke="#FFE0E6"
      />
      <path
        d="M242.625 55.2393L185.791 90.9332C178.978 95.2114 174.145 102.021 172.353 109.863C170.561 117.705 171.958 125.938 176.236 132.75C180.515 139.563 187.324 144.396 195.166 146.188C203.008 147.98 211.241 146.583 218.053 142.304L239.285 128.97C244.996 125.384 249.945 120.708 253.848 115.208C257.752 109.709 260.534 103.495 262.036 96.9201C263.538 90.3457 263.73 83.5396 262.601 76.8907C261.473 70.2421 259.046 63.8806 255.458 58.17C254.144 56.0807 252.054 54.5984 249.648 54.0489C247.242 53.4993 244.716 53.9275 242.625 55.2393Z"
        fill="#FFADBC"
      />
      <path
        d="M227.46 64.6027L185.257 91.1544C185.257 91.1544 180.52 94.5022 178.34 97.9043"
        stroke="#FFE0E6"
      />
    </svg>
  );
}
